"use client";

import { useState, useCallback, useRef } from "react";

export interface ZipLocation {
  lat: number;
  lng: number;
  city: string;
  state: string;
  stateSlug: string;
  postalCode: string;
}

interface UseZipGeocodingReturn {
  location: ZipLocation | null;
  isLoading: boolean;
  error: string | null;
  geocodeZip: (zip: string) => Promise<ZipLocation | null>;
  clearLocation: () => void;
}

const ZIP_REGEX = /^\d{5}$/;
const localCache = new Map<string, ZipLocation>();

const ZIP_LOOKUP_TIMEOUT_MS = 10000;

function lookupFailureMessage(status: number): string {
  if (status === 404) return "ZIP code not found";
  if (status === 429) return "Too many requests. Please wait.";
  return "Unable to look up ZIP code";
}

function toZipLocation(
  data: { latitude: number; longitude: number; city: string; state: string; stateSlug: string },
  postalCode: string,
): ZipLocation {
  return {
    lat: data.latitude,
    lng: data.longitude,
    city: data.city,
    state: data.state,
    stateSlug: data.stateSlug,
    postalCode,
  };
}

function replaceRequest(abortControllerRef: { current: AbortController | null }): AbortController {
  abortControllerRef.current?.abort();
  const controller = new AbortController();
  abortControllerRef.current = controller;
  return controller;
}

export function useZipGeocoding(): UseZipGeocodingReturn {
  const [location, setLocation] = useState<ZipLocation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const geocodeZip = useCallback(async (zip: string): Promise<ZipLocation | null> => {
    const fail = (message: string) => {
      setError(message);
      setLocation(null);
      return null;
    };
    const succeed = (result: ZipLocation) => {
      setLocation(result);
      setError(null);
      return result;
    };
    const normalizedZip = zip.trim();
    if (!ZIP_REGEX.test(normalizedZip)) return fail("Enter a valid 5-digit ZIP code");
    const cached = localCache.get(normalizedZip);
    if (cached) return succeed(cached);

    const controller = replaceRequest(abortControllerRef);
    const timeoutId = setTimeout(() => controller.abort(), ZIP_LOOKUP_TIMEOUT_MS);
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/postal-lookup?zip=${normalizedZip}`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) return fail(lookupFailureMessage(response.status));
      const result = toZipLocation(await response.json(), normalizedZip);
      localCache.set(normalizedZip, result);
      return succeed(result);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return null;
      return fail("Unable to look up ZIP code");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearLocation = useCallback(() => {
    setLocation(null);
    setError(null);
  }, []);

  return { location, isLoading, error, geocodeZip, clearLocation };
}
